import React, { useEffect } from "react";
import { Col, Form, Input, InputNumber, Modal, Row, Select } from "antd";
import zipCodes from "zipcodes";
import { StateCode } from "./hooks/useOrderDetailsEdit";

export interface PoolOwnerValues {
  poolOwnerFirstName: string;
  poolOwnerLastName: string;
  poolOwnerAddress: string;
  poolOwnerCity: string;
  poolOwnerState: string;
  poolOwnerZip: string;
}

interface PoolOwnerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: PoolOwnerValues) => void;
  initialValues: PoolOwnerValues;
  usStateOptions: StateCode[];
}

type PoolOwnerForm = PoolOwnerValues;

const PoolOwnerModal: React.FC<PoolOwnerModalProps> = ({
  open,
  onClose,
  onSave,
  initialValues,
  usStateOptions,
}) => {
  const [form] = Form.useForm<PoolOwnerForm>();
  const [confirmLoading, setConfirmLoading] = React.useState(false);

  useEffect(() => {
    if (open) form.setFieldsValue(initialValues);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleZipChange(val: string | number | null) {
    const strVal = val != null ? String(val) : "";
    const patch: Partial<PoolOwnerForm> = { poolOwnerZip: strVal };
    if (strVal.length >= 5) {
      const lookup = zipCodes.lookup(strVal);
      if (lookup) {
        patch.poolOwnerCity = lookup.city;
        patch.poolOwnerState = lookup.state;
      }
    }
    form.setFieldsValue(patch);
  }

  async function handleOk() {
    setConfirmLoading(true);
    try {
      const values = await form.validateFields();
      onSave({
        poolOwnerFirstName: values.poolOwnerFirstName ?? "",
        poolOwnerLastName: values.poolOwnerLastName ?? "",
        poolOwnerAddress: values.poolOwnerAddress ?? "",
        poolOwnerCity: values.poolOwnerCity ?? "",
        poolOwnerState: values.poolOwnerState ?? "",
        poolOwnerZip: values.poolOwnerZip ?? "",
      });
      onClose();
    } catch {
      // validation failed — stay open
    } finally {
      setConfirmLoading(false);
    }
  }

  return (
    <Modal
      title="Edit Pool Owner"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText="Confirm"
      confirmLoading={confirmLoading}
      destroyOnClose={false}
      centered
      width={480}
    >
      <Form
        form={form}
        layout="vertical"
        colon={false}
        preserve
        style={{ marginTop: 12, overflow: "hidden" }}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="poolOwnerFirstName" label="First Name" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 12 }}>
              <Input placeholder="John" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="poolOwnerLastName" label="Last Name" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 12 }}>
              <Input placeholder="Doe" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="poolOwnerAddress" label="Address" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 12 }}>
          <Input placeholder="1600 Pennsylvania Avenue" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={5}>
            <Form.Item name="poolOwnerZip" label="Zip" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 0 }}>
              <InputNumber
                placeholder="#####"
                onChange={handleZipChange}
                maxLength={5}
                stringMode
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="poolOwnerCity" label="City" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 0 }}>
              <Input placeholder="Washington" />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item name="poolOwnerState" label="State" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 0 }}>
              <Select placeholder="AZ">
                {usStateOptions.map((s) => (
                  <Select.Option key={s.id} value={s.id}>{s.id}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default PoolOwnerModal;
